import type { I18NMessages } from '@atlaskit/intl-messages-provider/common/types';

import defaultMessages from './en';

type AsyncLanguageLoader = () => Promise<{ default: I18NMessages }>;
type AsyncLanguages = Record<string, AsyncLanguageLoader>;

const createFetchMessagesForLocale = (
	asyncLanguages: AsyncLanguages,
	defaultMessages: I18NMessages,
) => {
	return async (locale: string): Promise<I18NMessages> => {
		try {
			// Fallback to the generic locale if specified one is not found
			const languageLocale = locale in asyncLanguages ? locale : locale.split(/[-_]/)[0] || '';

			// Dynamic import of the locale file
			const localeMessages = await asyncLanguages[languageLocale]?.();

			return localeMessages?.default ?? defaultMessages;
		} catch (e) {
			// ignore
		}

		return defaultMessages;
	};
};

const asyncLanguages = {
	cs: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-cs" */ './cs'
		),
	da: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-da" */ './da'
		),
	de: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-de" */ './de'
		),
	'en-GB': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-en_GB" */ './en_GB'
		),
	'en-ZZ': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-en_ZZ" */ './en_ZZ'
		),
	en: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-en" */ './en'
		),
	es: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-es" */ './es'
		),
	fi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-fi" */ './fi'
		),
	fr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-fr" */ './fr'
		),
	hu: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-hu" */ './hu'
		),
	it: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-it" */ './it'
		),
	ja: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-ja" */ './ja'
		),
	ko: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-ko" */ './ko'
		),
	nb: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-nb" */ './nb'
		),
	nl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-nl" */ './nl'
		),
	pl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-pl" */ './pl'
		),
	'pt-BR': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-pt_BR" */ './pt_BR'
		),
	ru: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-ru" */ './ru'
		),
	sv: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-sv" */ './sv'
		),
	th: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-th" */ './th'
		),
	tr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-tr" */ './tr'
		),
	uk: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-uk" */ './uk'
		),
	vi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-vi" */ './vi'
		),
	'zh-TW': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-zh_TW" */ './zh_TW'
		),
	zh: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/solution-templates-i18n-zh" */ './zh'
		),
} as const;

export const fetchMessagesForLocale: (locale: string) => Promise<I18NMessages> =
	createFetchMessagesForLocale(asyncLanguages, defaultMessages);

export { defaultMessages };
